
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CollaborationModalComponent } from '@core/components/collaboration-modal/collaboration-modal.component';
import { StatusService } from '@core/services/status.service';
import { TranslationService } from '@core/services/translation.service';
import { ApplicationApplicantFile, MyApplicationForUi } from '@core/typings/application.typing';
import { ApplicationStatuses } from '@core/typings/status.typing';
import { ApplicantCommunicationService } from '@features/applicant-communications/services/applicant-communications.service';
import { CommunicationForApplicant } from '@features/applicant-communications/typings/applicant-communications.typing';
import { ApplicationAttachmentService } from '@features/application-view/application-attachments/application-attachments.service';
import { ApplicationApplicantService } from '@features/apply/application-applicant.service';
import { CancelApplicationModalComponent } from '@features/cancel-application/cancel-application-modal/cancel-application-modal.component';
import { FormStatuses, MyApplicationForm } from '@features/configure-forms/form.typing';
import { CopyApplicationService } from '@features/copy-application/copy-application.service';
import { CyclesService } from '@features/cycles/cycles.service';
import { ProgramService } from '@features/programs/services/program.service';
import { TopLevelFilter } from '@yourcause/common';
import { ButtonAction } from '@yourcause/common/buttons';
import { DATE_TIME_FORMAT } from '@yourcause/common/date';
import { I18nService } from "@yourcause/common/i18n";
import { ConfirmationModalComponent, ModalFactory } from '@yourcause/common/modals';
import { map } from 'rxjs';

@Component({
    selector: 'gc-application-slat',
    templateUrl: './application-slat.component.html',
    styleUrls: ['./application-slat.component.scss'],
    standalone: false
})
export class ApplicationSlatComponent implements OnInit {
  @Input() application: MyApplicationForUi;
  @Input() simpleSlat = false;
  @Input() canEdit = true;
  @Input() isCopy = false;
  @Output() onSelectApplication = new EventEmitter();

  statusMap = this.statusService.applicationStatusMap;
  formStatusMap = this.statusService.formStatusMap;
  isNomination = false;
  FormStatuses = FormStatuses;
  portalTranslations = this.translationService.viewTranslations;
  programTranslationMap = this.portalTranslations.Grant_Program;
  formTranslationMap = this.portalTranslations.FormTranslation;
  topLevelFilters = [
    new TopLevelFilter(
      'text',
      'subject',
      '',
      this.i18n.translate('common:textSearchBySubject', {}, 'Search by subject')
    )
  ];
  AppStatuses = ApplicationStatuses;
  applicationIsDraft = true;
  dropdownActions: ButtonAction[] = [];
  commsOpen = false;
  DATE_TIME_FORMAT = DATE_TIME_FORMAT;
  applicationEmails$ = this.applicantCommunicationService.changesTo$('applicationCommunicationsMap').pipe(
    map((map) => {
      return map[this.application.applicationId] || [];
    })
  );
  commTableKey: string;

  constructor (
    private router: Router,
    private modalFactory: ModalFactory,
    private statusService: StatusService,
    private applicationApplicantService: ApplicationApplicantService,
    private translationService: TranslationService,
    private applicationAttachmentService: ApplicationAttachmentService,
    private copyApplicationService: CopyApplicationService,
    private i18n: I18nService,
    private applicantCommunicationService: ApplicantCommunicationService,
    private cyclesService: CyclesService,
    private programService: ProgramService
  ) { }

  ngOnInit () {
    this.applicationIsDraft = this.application.status === this.AppStatuses.Draft;
    this.isNomination = !!this.application.nominee;
    this.commTableKey = this.applicantCommunicationService.getApplicationCommunicationTableKey(this.application.applicationId);
    this.setDropdownActions();
  }

  setDropdownActions () {
    this.dropdownActions = [this.application.canManageApplicants ?
      {
        i18nKey: this.isNomination ?
          'APPLY:textManageNominators' :
          'common:textManageApplicants',
        i18nDefault: this.isNomination ?
          'Manage nominators' :
          'Manage applicants',
        icon: 'user-plus',
        iconClass: 'text-success',
        onClick: () => {
          this.collaborationModal();
        }
      } :
      undefined, this.application.canCopy ? {
        i18nKey: 'common:textCopy',
        i18nDefault: 'Copy',
        icon: 'copy',
        onClick: () => {
          this.copyApplication();
        }
      } : undefined, this.application.canCancel ? {
        i18nKey: 'common:btnCancel',
        i18nDefault: 'Cancel',
        icon: 'trash-alt',
        iconClass: 'text-warning',
        onClick: () => {
          this.cancelModal();
        }
      } : undefined, this.application.canDelete ? {
        i18nKey: 'common:btnDelete',
        i18nDefault: 'Delete',
        icon: 'times',
        iconClass: 'text-danger',
        onClick: () => {
          this.deleteModal();
        }
      } : undefined].filter((item) => !!item);
  }

  async collaborationModal () {
    await this.modalFactory.open(
      CollaborationModalComponent,
      {
        applicationId: this.application.applicationId,
        sendNotificationsByDefault: this.application.programSendEmailsToCollaborators,
        isNomination: this.isNomination,
        clientId: this.application.clientId,
        clientName: this.application.clientName
      }
    );
  }

  async toggleComms () {
    this.commsOpen = !this.commsOpen;
    if (this.commsOpen) {
      await this.applicantCommunicationService.setApplicationCommunications(this.application.applicationId);
    }
  }

  openFlyout (comm: CommunicationForApplicant) {
    this.applicantCommunicationService.openCommunicationFlyout(comm, this.application);
  }

  async deleteModal () {
    await this.applicationApplicantService.handleDeleteApplication(
      this.application.applicationId,
      this.isNomination
    );
  }

  async cancelModal () {
    const cancelModalResponse = await this.modalFactory.open(
      CancelApplicationModalComponent,
      {
        applicationId: this.application.applicationId,
        programId: this.application.grantProgramId,
        isNomination: this.isNomination,
        isManager: false,
        programName: this.application.grantProgramName,
        currentlyDraftStatus: this.application.status === ApplicationStatuses.Draft
      }
    );
    if (cancelModalResponse) {
      await this.applicationApplicantService.cancelApplication(cancelModalResponse);
    }
  }

  goToApplication (form: MyApplicationForm) {
    this.router.navigateByUrl(
      `/apply/application/${this.application.applicationId}/forms/${form.formId}`
    );
  }

  selectApplication () {
    this.onSelectApplication.emit();
  }

  async copyApplication () {
    const program = await this.programService.handleGetProgramForApplicant(this.application.grantProgramGuid);
    const cyclesForDisplay = this.cyclesService.getCycleDateHelpers(program.cycles);
    const currentCycle = cyclesForDisplay.currentCycle;
    await this.copyApplicationService.handleCopyApplication(
      this.application.applicationId,
      this.application.grantProgramId,
      currentCycle?.id ?? this.application.cycleId,
      this.application.organizationId,
      this.isNomination,
      false,
      this.application.clientId
    );
  }

  async openFile (file: ApplicationApplicantFile) {
    await this.applicationAttachmentService.handleDownloadFile(
      file.fileUploadId,
      null,
      null,
      file.fileName
    );
  }

  async viewGuidelines (): Promise<void> {
    const guidelinesTranslation = !!this.programTranslationMap[this.application.grantProgramId]?.Guidelines ?
      this.programTranslationMap[this.application.grantProgramId]?.Guidelines :
      this.application.grantProgramGuidelines;
    await this.modalFactory.open(
      ConfirmationModalComponent,
      {
        confirmText: guidelinesTranslation,
        modalHeader: this.i18n.translate(
          'PROGRAM:textProgramGuidelinesTitle',
          {},
          'Program Guidelines'
        ),
        useRichTextDisplay: true
      }
    );
  }
}
